<template>
  <!--
    This switch owns sm/md/lg track geometry, the success tone, and the
    update:modelValue + zero-argument toggleRequested contract through the
    structure/skin/interaction planes. USwitch adoption requires support for
    this geometry and contract without per-consumer CSS.
  -->
  <button
    :id="id === '' ? undefined : id"
    type="button"
    role="switch"
    :aria-checked="modelValue"
    :aria-label="ariaLabel"
    :title="title === '' ? undefined : title"
    :disabled="disabled"
    :class="[
      structure['root'],
      skin['root'],
      interaction['root'],
      disabled ? skin['root-disabled'] : ''
    ]"
    @click="handleClick"
  >
    <!--
      Optional in-switch content, e.g. a visible status label. It sits inside
      the button so the whole control stays one click target.
    -->
    <slot />

    <span
      aria-hidden="true"
      :class="[
        structure['track'],
        interaction['track'],
        trackSizeClass,
        modelValue ? onTrackClass : offTrackClass
      ]"
    >
      <span
        :class="[
          structure['thumb'],
          skin['thumb'],
          interaction['thumb'],
          thumbSizeClass,
          modelValue ? onThumbClass : structure['thumb-off']
        ]"
      >
        <!-- Optional knob content, e.g. a check mark while on. -->
        <slot name="thumb" />
      </span>
    </span>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import structure from './Toggle.structure.module.css'
import skin from './Toggle.skin.module.css'
import interaction from './Toggle.interaction.module.css'

const props = withDefaults(
  defineProps<{
    /** Current on/off state. */
    modelValue: boolean
    /** Accessible name announced with the switch role. */
    ariaLabel: string
    /**
     * Track geometry for each supported size:
     * `sm` 20x36 with a 16 thumb (notification settings), `md` 24x44 with a 20
     * thumb (store availability), `lg` 32x56 with a 24 thumb (menu status).
     */
    size?: 'lg' | 'md' | 'sm'
    /**
     * Colour of the on state. `success` is the status-toggle tone, kept as
     * configuration rather than per-consumer CSS.
     */
    tone?: 'primary' | 'success'
    /** Prevents interaction. */
    disabled?: boolean
    /** Optional id, for a label's `for` attribute. */
    id?: string
    /** Native tooltip text, e.g. why a required switch cannot be turned off. */
    title?: string
  }>(),
  { disabled: false, id: '', size: 'md', title: '', tone: 'primary' }
)

const emit = defineEmits<{ 'update:modelValue': [value: boolean]; toggleRequested: [] }>()

defineSlots<{
  /** Content rendered inside the button, beside the track. */
  default?: () => unknown
  /** Content rendered inside the thumb. */
  thumb?: () => unknown
}>()

// Resolved in their own computed rather than a lookup map:
// `atomic-style-ownership` traces only member expressions with literal keys,
// and `no-nested-ternary` blocks the chained form.
const trackSizes = { lg: 'track-lg', md: 'track-md', sm: 'track-sm' } as const
const thumbSizes = { lg: 'thumb-lg', md: 'thumb-md', sm: 'thumb-sm' } as const
const thumbTravel = { lg: 'thumb-on-lg', md: 'thumb-on-md', sm: 'thumb-on-sm' } as const
const offTracks = { lg: 'track-off-lg', md: 'track-off-md', sm: 'track-off-sm' } as const

const trackSizeClass = computed(() => structure[trackSizes[props.size]])
const thumbSizeClass = computed(() => structure[thumbSizes[props.size]])
const onThumbClass = computed(() => structure[thumbTravel[props.size]])
const offTrackClass = computed(() => skin[offTracks[props.size]])
const onTrackClass = computed(() =>
  props.tone === 'success' ? skin['track-on-success'] : skin['track-on']
)

const handleClick = (): void => {
  if (props.disabled) return

  // Model updates precede the payload-free request for caller-owned state.
  emit('update:modelValue', !props.modelValue)
  emit('toggleRequested')
}
</script>
